/**
 * Beam Renderer for Kobayashi Maru
 * Renders beam weapon visual effects using PixiJS Graphics
 */
import { Application, Graphics, Container } from 'pixi.js';
import { BeamVisual } from '../systems/combatSystem';
import { TurretType } from '../types/constants';
import { COMBAT_CONFIG, RENDERING_CONFIG } from '../config';

// Colors for different beam types — sourced from centralized config
const BEAM_COLORS = {
  [TurretType.PHASER_ARRAY]: COMBAT_CONFIG.BEAM.COLORS.PHASER,
  [TurretType.DISRUPTOR_BANK]: COMBAT_CONFIG.BEAM.COLORS.DISRUPTOR,
  [TurretType.TORPEDO_LAUNCHER]: COMBAT_CONFIG.BEAM.COLORS.TORPEDO,
  [TurretType.TETRYON_BEAM]: COMBAT_CONFIG.BEAM.COLORS.TETRYON,
  [TurretType.PLASMA_CANNON]: COMBAT_CONFIG.BEAM.COLORS.PLASMA,
  [TurretType.POLARON_BEAM]: COMBAT_CONFIG.BEAM.COLORS.POLARON,
} as const;

/**
 * Charge effect for beam weapons
 */
export interface ChargeEffect {
  turretId: number;
  x: number;
  y: number;
  progress: number;  // 0-1
  duration: number;
  color: number;
}

// Charging effect constants — from centralized config
const MAX_CHARGE_RADIUS = COMBAT_CONFIG.BEAM.MAX_CHARGE_RADIUS;
const BASE_CORE_ALPHA = COMBAT_CONFIG.BEAM.BASE_CORE_ALPHA;
const PULSE_AMPLITUDE = COMBAT_CONFIG.BEAM.PULSE_AMPLITUDE;
const PULSE_FREQUENCY = COMBAT_CONFIG.BEAM.PULSE_FREQUENCY;

// Beam rendering layer constants — from centralized config
const BEAM = RENDERING_CONFIG.BEAM_RENDERING;

/**
 * BeamRenderer renders weapon beam effects
 */
export class BeamRenderer {
  private app: Application;
  private graphics: Graphics;
  private container: Container;
  private initialized: boolean = false;
  private glowContainer: Container | null = null;
  private charges: Map<number, ChargeEffect> = new Map();

  constructor(app: Application) {
    this.app = app;
    this.graphics = new Graphics();
    this.container = new Container();
  }

  /**
   * Initialize the beam renderer
   * @param glowContainer Optional glow container for rendering beams with glow effects
   */
  init(glowContainer?: Container): void {
    if (this.initialized) {
      return;
    }

    this.glowContainer = glowContainer || null;
    this.container.addChild(this.graphics);
    
    // Add to glow container if provided, otherwise add to stage
    if (this.glowContainer) {
      this.glowContainer.addChild(this.container);
    } else {
      this.app.stage.addChild(this.container);
    }

    this.initialized = true;
  }

  /**
   * Get beam color for a turret type
   */
  private getBeamColor(turretType: number): number {
    switch (turretType) {
      case TurretType.PHASER_ARRAY:
        return BEAM_COLORS[TurretType.PHASER_ARRAY];
      case TurretType.DISRUPTOR_BANK:
        return BEAM_COLORS[TurretType.DISRUPTOR_BANK];
      case TurretType.TETRYON_BEAM:
        return BEAM_COLORS[TurretType.TETRYON_BEAM];
      case TurretType.PLASMA_CANNON:
        return BEAM_COLORS[TurretType.PLASMA_CANNON];
      case TurretType.POLARON_BEAM:
        return BEAM_COLORS[TurretType.POLARON_BEAM];
      default:
        return BEAM_COLORS[TurretType.PHASER_ARRAY];
    }
  }

  /**
   * Start charging effect for a turret
   * @param turretId - Entity ID of the turret
   * @param x - X position of the turret
   * @param y - Y position of the turret
   * @param duration - Duration of the charge in seconds
   * @param turretType - Type of turret (for color)
   */
  startCharge(turretId: number, x: number, y: number, duration: number, turretType: number): void {
    const color = this.getBeamColor(turretType);
    this.charges.set(turretId, {
      turretId,
      x,
      y,
      progress: 0,
      duration,
      color
    });
  }

  /**
   * Update charging effects
   * @param deltaTime - Time elapsed since last frame in seconds
   */
  updateCharges(deltaTime: number): void {
    for (const [turretId, charge] of this.charges.entries()) {
      charge.progress += deltaTime / charge.duration;
      
      // Remove completed charges
      if (charge.progress >= 1.0) {
        this.charges.delete(turretId);
      }
    }
  }

  /**
   * Render all active charging effects
   */
  private renderCharges(): void {
    for (const charge of this.charges.values()) {
      const radius = MAX_CHARGE_RADIUS * charge.progress;
      const alpha = (1 - charge.progress) * BEAM.CHARGE.ALPHA_MULTIPLIER;

      // Draw expanding charge ring
      this.graphics.circle(charge.x, charge.y, radius);
      this.graphics.stroke({ width: BEAM.CHARGE.STROKE_WIDTH, color: charge.color, alpha });

      // Draw pulsing core
      const coreAlpha = BASE_CORE_ALPHA + Math.sin(charge.progress * Math.PI * PULSE_FREQUENCY) * PULSE_AMPLITUDE;
      this.graphics.circle(charge.x, charge.y, BEAM.CHARGE.CORE_RADIUS);
      this.graphics.fill({ color: charge.color, alpha: coreAlpha });
    }
  }

  /**
   * Render beam visuals for the current frame
   * @param beams Array of beam visuals to render
   */
  render(beams: BeamVisual[]): void {
    if (!this.initialized) {
      return;
    }

    // Clear previous frame's graphics
    this.graphics.clear();

    // Render charging effects first (underneath beams)
    this.renderCharges();

    for (const beam of beams) {
      const color = this.getBeamColor(beam.turretType);
      const intensity = beam.intensity || 1.0;
      
      // Render beam segments with electricity effect
      if (beam.segments && beam.segments.length > 0) {
        this.renderSegmentedBeam(beam, color, intensity);
      } else {
        // Fallback to simple beam if no segments
        this.renderSimpleBeam(beam, color, intensity);
      }
      
      // Add impact effect at beam endpoint
      this.renderImpactEffect(beam.endX, beam.endY, color, intensity);
    }
  }

  /**
   * Render a segmented beam with electricity jitter
   */
  private renderSegmentedBeam(beam: BeamVisual, color: number, intensity: number): void {
    // Draw outer glow layer (widest, most transparent)
    for (const segment of beam.segments) {
      this.graphics.moveTo(segment.startX, segment.startY);
      this.graphics.lineTo(segment.endX, segment.endY);
      this.graphics.stroke({ width: BEAM.OUTER_GLOW.WIDTH, color, alpha: BEAM.OUTER_GLOW.ALPHA * intensity });
    }

    // Draw middle glow layer
    for (const segment of beam.segments) {
      this.graphics.moveTo(segment.startX, segment.startY);
      this.graphics.lineTo(segment.endX, segment.endY);
      this.graphics.stroke({ width: BEAM.MIDDLE_GLOW.WIDTH, color, alpha: BEAM.MIDDLE_GLOW.ALPHA * intensity });
    }

    // Draw main beam segments (brightest)
    for (const segment of beam.segments) {
      this.graphics.moveTo(segment.startX, segment.startY);
      this.graphics.lineTo(segment.endX, segment.endY);
      this.graphics.stroke({ width: BEAM.MAIN_BEAM.WIDTH, color, alpha: BEAM.MAIN_BEAM.ALPHA * intensity });
    }

    // Draw core bright line (thinnest, brightest)
    for (const segment of beam.segments) {
      this.graphics.moveTo(segment.startX, segment.startY);
      this.graphics.lineTo(segment.endX, segment.endY);
      this.graphics.stroke({ width: BEAM.CORE.WIDTH, color: BEAM.CORE.COLOR, alpha: BEAM.CORE.ALPHA * intensity });
    }
  }

  /**
   * Render a simple beam without segments (fallback)
   */
  private renderSimpleBeam(beam: BeamVisual, color: number, intensity: number): void {
    // Draw glow effect first (wider, more transparent line underneath)
    this.graphics.moveTo(beam.startX, beam.startY);
    this.graphics.lineTo(beam.endX, beam.endY);
    this.graphics.stroke({ width: BEAM.SIMPLE_GLOW.WIDTH, color, alpha: BEAM.SIMPLE_GLOW.ALPHA * intensity });

    // Draw main beam line on top
    this.graphics.moveTo(beam.startX, beam.startY);
    this.graphics.lineTo(beam.endX, beam.endY);
    this.graphics.stroke({ width: BEAM.SIMPLE_MAIN.WIDTH, color, alpha: BEAM.SIMPLE_MAIN.ALPHA * intensity });
  }

  /**
   * Render impact effect at beam endpoint
   */
  private renderImpactEffect(x: number, y: number, color: number, intensity: number): void {
    // Draw impact glow
    this.graphics.circle(x, y, BEAM.IMPACT_GLOW.RADIUS);
    this.graphics.fill({ color, alpha: BEAM.IMPACT_GLOW.ALPHA * intensity });

    // Draw bright impact core
    this.graphics.circle(x, y, BEAM.IMPACT_CORE.RADIUS);
    this.graphics.fill({ color: BEAM.IMPACT_CORE.COLOR, alpha: BEAM.IMPACT_CORE.ALPHA * intensity });
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.graphics.destroy();
    this.container.destroy({ children: true });
    this.initialized = false;
  }
}
